export const DASHBOARD_QUICK_ACTIONS_LIMIT = 4;

export const DASHBOARD_QUICK_ACTIONS_DEFAULT = Object.freeze([
  'run-automation',
  'new-schedule',
  'upload-package',
  'register-executor',
]);

/**
 * Allowlist of dashboard quick actions.
 *
 * Every entry names a route `App.js` really registers, so a shortcut can never
 * offer a page that does not exist. Mirrored by
 * `DASHBOARD_QUICK_ACTIONS_REGISTRY` in
 * `backend/app/services/dashboard_quick_actions_service.py`; a backend test
 * fails if the two drift.
 *
 * The access fields document each destination's gate — they are the shared
 * contract with the backend registry, NOT a filter applied here. Prototype
 * parity (PBI #3968) shows every shortcut to every profile and lets the
 * route's own `ProtectedRoute` bounce what the session cannot open:
 *
 * - `permission`: paired permission, or `null` when the route gates on the
 *   admin role list alone.
 * - `adminRoles`: the route declares `HUB_ROUTE_ADMIN_ROLE_LIST`, so an admin
 *   satisfies it without the permission (`ProtectedRoute`'s either/or rule).
 * - `sectionKey`: sidebar section the destination belongs to.
 *
 * Ids avoid SQL keywords (`new-schedule`, not `create-schedule`): the backend
 * security middleware scans the PUT body and answers 400 on the bare word
 * `CREATE`. Labels still read "Criar horário".
 */
export const DASHBOARD_QUICK_ACTIONS_CATALOG = Object.freeze([
  {
    id: 'run-automation',
    labelKey: 'dashboard.quickActionCatalog.runAutomation.label',
    descriptionKey: 'dashboard.quickActionCatalog.runAutomation.description',
    route: 'automations',
    permission: 'job:read',
    adminRoles: false,
    sectionKey: 'automation',
  },
  {
    id: 'new-automation',
    labelKey: 'dashboard.quickActionCatalog.newAutomation.label',
    descriptionKey: 'dashboard.quickActionCatalog.newAutomation.description',
    route: 'automations/create',
    permission: 'job:create',
    adminRoles: false,
    sectionKey: 'automation',
  },
  {
    id: 'view-schedules',
    labelKey: 'dashboard.quickActionCatalog.viewSchedules.label',
    descriptionKey: 'dashboard.quickActionCatalog.viewSchedules.description',
    route: 'schedules',
    permission: 'schedule:read',
    adminRoles: false,
    sectionKey: 'automation',
  },
  {
    id: 'new-schedule',
    labelKey: 'dashboard.quickActionCatalog.newSchedule.label',
    descriptionKey: 'dashboard.quickActionCatalog.newSchedule.description',
    route: 'automations?tab=schedules&create=1',
    permission: 'schedule:create',
    adminRoles: false,
    sectionKey: 'automation',
  },
  {
    id: 'view-packages',
    labelKey: 'dashboard.quickActionCatalog.viewPackages.label',
    descriptionKey: 'dashboard.quickActionCatalog.viewPackages.description',
    route: 'packages',
    permission: 'package:read',
    adminRoles: false,
    sectionKey: 'infrastructure',
  },
  {
    id: 'upload-package',
    labelKey: 'dashboard.quickActionCatalog.uploadPackage.label',
    descriptionKey: 'dashboard.quickActionCatalog.uploadPackage.description',
    route: 'packages/upload',
    permission: 'package:create',
    adminRoles: false,
    sectionKey: 'infrastructure',
  },
  {
    id: 'view-executors',
    labelKey: 'dashboard.quickActionCatalog.viewExecutors.label',
    descriptionKey: 'dashboard.quickActionCatalog.viewExecutors.description',
    route: 'executors',
    permission: 'agent:read',
    adminRoles: true,
    sectionKey: 'infrastructure',
  },
  {
    id: 'register-executor',
    labelKey: 'dashboard.quickActionCatalog.registerExecutor.label',
    descriptionKey: 'dashboard.quickActionCatalog.registerExecutor.description',
    route: 'executors/register',
    permission: 'agent:create',
    adminRoles: true,
    sectionKey: 'infrastructure',
  },
  {
    id: 'view-agents',
    labelKey: 'dashboard.quickActionCatalog.viewAgents.label',
    descriptionKey: 'dashboard.quickActionCatalog.viewAgents.description',
    route: 'ai-agents',
    permission: 'job:read',
    adminRoles: false,
    sectionKey: 'dashboard',
  },
  {
    id: 'manage-skills',
    labelKey: 'dashboard.quickActionCatalog.manageSkills.label',
    descriptionKey: 'dashboard.quickActionCatalog.manageSkills.description',
    route: 'skills',
    permission: null,
    adminRoles: true,
    sectionKey: 'administration',
  },
  {
    id: 'manage-connections',
    labelKey: 'dashboard.quickActionCatalog.manageConnections.label',
    descriptionKey: 'dashboard.quickActionCatalog.manageConnections.description',
    route: 'mcps',
    permission: 'tenant:read',
    adminRoles: true,
    sectionKey: 'administration',
  },
]);

export function getDashboardQuickActionById(actionId) {
  return DASHBOARD_QUICK_ACTIONS_CATALOG.find((action) => action.id === actionId) || null;
}

export function normalizeDashboardQuickActionsSelection(value) {
  if (!Array.isArray(value)) {
    return [...DASHBOARD_QUICK_ACTIONS_DEFAULT];
  }

  const unique = [];
  value.forEach((item) => {
    const actionId = String(item || '').trim();
    if (!actionId || unique.includes(actionId) || !getDashboardQuickActionById(actionId)) {
      return;
    }
    unique.push(actionId);
  });

  return unique.slice(0, DASHBOARD_QUICK_ACTIONS_LIMIT);
}

